import { EnhancedPattern } from '@/types/trading-analysis-types';

export class WeightCalculator {
  /**
   * Calculate recency weight based on pattern's last occurrence
   */
  static calculateRecencyWeight(
    lastSeenIndex: number, 
    totalLength: number, 
    decayFactor: number = 0.1
  ): number {
    if (lastSeenIndex < 0 || totalLength <= 0) return 0;
    
    // More recent = higher weight
    const recencyRatio = lastSeenIndex / totalLength;
    return Math.exp(-decayFactor * (1 - recencyRatio));
  }

  /**
   * Calculate length weight - longer patterns are more reliable
   */
  static calculateLengthWeight(patternLength: number, maxLength: number = 12): number {
    if (patternLength <= 0) return 0;
    
    // Logarithmic scaling to prevent very long patterns from dominating
    const normalizedLength = Math.min(patternLength, maxLength) / maxLength;
    return Math.log(1 + normalizedLength * Math.E);
  }

  /**
   * Calculate frequency weight using logarithmic scaling
   */
  static calculateFrequencyWeight(frequency: number): number {
    if (frequency <= 0) return 0;
    
    // Logarithmic scaling to prevent very frequent patterns from dominating
    return Math.log(1 + frequency);
  }

  /**
   * Calculate success rate weight with confidence adjustment
   */
  static calculateSuccessRateWeight(
    successRate: number, 
    sampleSize: number,
    confidenceLevel: number = 0.95
  ): number {
    if (sampleSize <= 0) return 0;
    
    // Adjust success rate based on sample size confidence
    const confidenceAdjustment = Math.min(1, sampleSize / 10); // Full confidence at 10+ samples
    const adjustedSuccessRate = successRate * confidenceAdjustment;
    
    // Apply sigmoid function to emphasize high success rates
    return 1 / (1 + Math.exp(-6 * (adjustedSuccessRate / 100 - 0.5)));
  }

  /**
   * Calculate combined weight for a pattern
   */
  static calculateCombinedWeight(
    pattern: EnhancedPattern,
    totalSequenceLength: number,
    weights: {
      recency: number;
      length: number;
      frequency: number;
      successRate: number;
    } = { recency: 0.25, length: 0.25, frequency: 0.25, successRate: 0.25 }
  ): number {
    const recencyWeight = pattern.lastSeenIndex !== undefined 
      ? this.calculateRecencyWeight(pattern.lastSeenIndex, totalSequenceLength)
      : 0;
    
    const lengthWeight = this.calculateLengthWeight(pattern.pattern.length);
    const frequencyWeight = this.calculateFrequencyWeight(pattern.frequency);
    const successRateWeight = this.calculateSuccessRateWeight(
      pattern.successRate, 
      pattern.nextWinCount + pattern.nextLossCount
    );

    // Weighted combination
    return (
      recencyWeight * weights.recency +
      lengthWeight * weights.length +
      frequencyWeight * weights.frequency +
      successRateWeight * weights.successRate
    );
  }

  /**
   * Apply weights to prediction calculation
   */
  static calculateWeightedPrediction(patterns: EnhancedPattern[]): {
    winProbability: number;
    lossProbability: number;
    totalWeight: number;
  } {
    if (patterns.length === 0) {
      return { winProbability: 50, lossProbability: 50, totalWeight: 0 };
    }

    let totalWeightedWins = 0;
    let totalWeightedLosses = 0;
    let totalWeight = 0;

    for (const pattern of patterns) {
      // Use stored weights or calculate on-the-fly
      const weight = pattern.recencyWeight && pattern.lengthWeight 
        ? (pattern.recencyWeight + pattern.lengthWeight) / 2
        : this.calculateCombinedWeight(pattern, 100); // Default sequence length

      const patternWeight = weight * (pattern.confidenceScore / 100);
      
      totalWeightedWins += pattern.nextWinCount * patternWeight;
      totalWeightedLosses += pattern.nextLossCount * patternWeight;
      totalWeight += patternWeight;
    }

    if (totalWeight === 0) {
      return { winProbability: 50, lossProbability: 50, totalWeight: 0 };
    }

    const totalWeightedOutcomes = totalWeightedWins + totalWeightedLosses;
    const winProbability = (totalWeightedWins / totalWeightedOutcomes) * 100;
    const lossProbability = (totalWeightedLosses / totalWeightedOutcomes) * 100;

    return {
      winProbability: Math.round(winProbability * 100) / 100,
      lossProbability: Math.round(lossProbability * 100) / 100,
      totalWeight
    };
  }

  /**
   * Update pattern with calculated weights
   */
  static enhancePatternWithWeights(
    pattern: EnhancedPattern,
    totalSequenceLength: number
  ): EnhancedPattern {
    const recencyWeight = pattern.lastSeenIndex !== undefined 
      ? this.calculateRecencyWeight(pattern.lastSeenIndex, totalSequenceLength)
      : undefined;
    
    const lengthWeight = this.calculateLengthWeight(pattern.pattern.length);

    return {
      ...pattern,
      recencyWeight,
      lengthWeight,
    };
  }
}